import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Mic, MicOff, Volume2, Loader2, X } from "lucide-react";
import "./ModernVoiceChatInterface.css";

export default function ModernVoiceChatInterface({ 
  onClose,
  isActive,
  isListening,
  isSpeaking,
  isProcessing,
  currentTranscript,
  startVoiceChat,
  stopVoiceChat,
  interruptAI,
  error
}) {

  const [showInterface, setShowInterface] = useState(false);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    setShowInterface(true);
  }, []);

  // Audio visualization for the circular waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 80;

    let animationId;
    let time = 0;

    const animate = () => {
      time += 0.02;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw circular waveform based on state
      if (isListening || isSpeaking || isProcessing) {
        const numBars = 60;
        const angleStep = (Math.PI * 2) / numBars;

        for (let i = 0; i < numBars; i++) {
          const angle = i * angleStep;
          let barHeight = 8;

          if (isListening) {
            // Gentle pulsing animation for listening
            barHeight = 8 + Math.sin(time * 2 + i * 0.1) * 4;
          } else if (isSpeaking) {
            // Same animation as processing for speaking
            barHeight = 10 + Math.sin(time * 5 + i * 0.3) * 6;
          } else if (isProcessing) {
            // Rapid processing animation
            barHeight = 10 + Math.sin(time * 5 + i * 0.3) * 6;
          }

          const innerRadius = radius - 5;
          const outerRadius = innerRadius + barHeight;

          const x1 = centerX + Math.cos(angle) * innerRadius;
          const y1 = centerY + Math.sin(angle) * innerRadius;
          const x2 = centerX + Math.cos(angle) * outerRadius;
          const y2 = centerY + Math.sin(angle) * outerRadius;

          // Set color based on state
          let color = '#34d094'; // Green for listening
          if (isSpeaking) color = '#4a8df8'; // Blue for AI speaking
          else if (isProcessing) color = '#f59e0b';

          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.globalAlpha = 0.7 + Math.sin(time + i * 0.1) * 0.3;
          
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    if (isActive) {
      animate();
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isActive, isListening, isSpeaking, isProcessing]);

  const handleToggleVoiceChat = () => {
    if (isSpeaking && interruptAI) {
      // Tap while the AI is talking = interrupt it, not end the session.
      interruptAI();
    } else if (isActive) {
      stopVoiceChat();
    } else {
      startVoiceChat();
    }
  };

  const handleClose = () => {
    if (isActive) {
      stopVoiceChat();
    }
    setShowInterface(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const getStatusText = () => {
    if (isListening) return "Listening... speak now";
    if (isSpeaking) return "AI is speaking";
    if (isProcessing) return "Processing your request";
    if (isActive) return "Voice chat active";
    return "Start voice chat";
  };

  const getStatusSubtext = () => {
    if (isListening) return "Speak clearly into your microphone";
    if (isSpeaking) return "Tap the button or start talking to interrupt";
    if (isProcessing) return "Analyzing your voice input";
    return "Click the button to begin";
  };

  return createPortal(
    <div className={`modern-voice-chat-overlay ${showInterface ? 'show' : ''}`}>
      <div className="modern-voice-chat-container">
        {/* Header */}
        <div className="modern-voice-chat-header">
          <h2>Medical Expert</h2>
        </div>

        {/* Main circular interface */}
        <div className="modern-voice-chat-main">
          <div className="modern-voice-chat-circle-container">
            {/* Animated waveform canvas */}
            <canvas 
              ref={canvasRef}
              className="modern-voice-chat-waveform-canvas"
              width={200}
              height={200}
            />
            
            {/* Central button */}
            <button
              className={`modern-voice-chat-button ${isActive ? 'active' : ''} ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''} ${isProcessing ? 'processing' : ''}`}
              onClick={handleToggleVoiceChat}
              disabled={isProcessing}
            >
              <div className="modern-voice-chat-button-inner">
                {isProcessing ? (
                  <Loader2 className="animate-spin" size={32} />
                ) : isActive ? (
                  isSpeaking ? <Volume2 size={32} /> : <Mic size={32} />
                ) : (
                  <Mic size={32} />
                )}
              </div>
            </button>
          </div>

          {/* Status text */}
          <div className="modern-voice-chat-status">
            <h3 className="modern-voice-chat-status-title">
              {getStatusText()}
            </h3>
            <p className="modern-voice-chat-status-subtitle">
              {getStatusSubtext()}
            </p>
          </div>
        </div>

        {/* Close button - moved outside and below the main div */}
        <button 
          className="modern-voice-chat-close"
          onClick={handleClose}
          aria-label="Close voice chat"
        >
          <X size={24} />
        </button>



        {/* Error display */}
        {error && (
          <div className="modern-voice-chat-error">
            <div className="modern-voice-chat-error-text">
              {typeof error === 'string' ? error : error.message || 'An error occurred'}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}